import { create } from "zustand/react";
import {
  GenericUpdateAction,
  UpdateAction,
  UpdateActionType,
  UpdateActionWithState,
} from "@/models/socketIo/updateAction";
import {
  defaultGameState,
  getUpdateFunction,
  isDecidingSet,
} from "./gameZustandUpdaters";
import { ConnectionStatus } from "@/models/ticker";
import {
  UpdateActionHistory,
  UpdateActionWithStateDict,
} from "@/models/socketIo/socketIo";
import { GameState, UpdateGameStateFunction } from "@/models/gameState";
import { otherTeam } from "@/pdf/utils";
import { useAppDataContext } from "@context/AppContext";
import { TeamReference } from "@/models/pdfData";
import { formatScore } from "@context/utils";
import { generateUuidHistoryChecksum } from "@/utils/history-checksum";
import { isVisibleActionType } from "@components/event-history/event-history-utils";

interface GameStateZustand extends GameState {
  connectionStatus: ConnectionStatus;
  latency: number | null;
  setConnectionStatus: (connectionStatus: Partial<ConnectionStatus>) => void;
  setLatency: (latency: number | null) => void;
  getCurrentScoreForTeam: (team?: TeamReference) => string;
}

export const gameState = <T extends keyof GameStateZustand>(
  key: T,
): GameStateZustand[T] => useGameState((s) => s[key]);

export const useTeamSides = (
  appDataContext: Pick<
    ReturnType<typeof useAppDataContext>,
    "useFirstRefPerspective"
  >,
) => {
  const teamSides = gameState("teamSides");
  const currentSet = gameState("currentSet");
  const currentScore = gameState("currentScore");
  const { setsToWin, pointsToWinDecidingSet } = gameState("matchConfiguration");
  const pointsAtSideChange = Math.ceil(pointsToWinDecidingSet / 2);

  let switchReasonsCounter = appDataContext.useFirstRefPerspective ? 0 : 1;
  switchReasonsCounter +=
    isDecidingSet(currentSet.setNumber, setsToWin) &&
    (currentScore.team1 >= pointsAtSideChange ||
      currentScore.team2 >= pointsAtSideChange)
      ? 1
      : 0;

  return switchReasonsCounter % 2 === 0
    ? {
        leftTeam: otherTeam(teamSides.leftTeam),
        rightTeam: otherTeam(teamSides.rightTeam),
      }
    : { ...teamSides };
};

export const useGameState = create<GameStateZustand>()((set, get) => ({
  ...defaultGameState(),
  connectionStatus: "lost",
  latency: null,
  setLatency: (latency: number | null) => {
    if (!latency || latency > 0) {
      set({ latency });
    }
  },
  setConnectionStatus: (status: ConnectionStatus) => {
    const currentStatus = get().connectionStatus;
    if (currentStatus !== status) {
      console.debug("changing status", currentStatus, "->", status);
      set({ connectionStatus: status });
    }
  },
  getCurrentScoreForTeam: (team) => {
    if (get().currentScore) {
      return formatScore(get().currentScore, team);
    } else {
      return "-";
    }
  },
}));

export const buildGameState = (actions: UpdateActionHistory) =>
  useGameState.setState((state) => ({
    ...buildGameStateWithActions(actions),
    connectionStatus: state.connectionStatus,
  }));

export const reBuildGameState = (action: UpdateAction) =>
  useGameState.setState((state) => {
    const updatedActionHistory = {
      ...state.actionHistory,
      [action.orderRank]: action,
    };
    const newState = buildGameStateWithActions(updatedActionHistory);
    return { ...newState, connectionStatus: state.connectionStatus };
  });

const buildGameStateWithActions = (
  actionHistory: UpdateActionHistory,
): GameStateZustand => {
  const undoActionTypes: UpdateActionType[] = [
    "[Undoable] Undo last action",
    "[Undoable] Redo previous action",
  ];

  const undoableActionTypes: UpdateActionType[] = [
    "[Match] Edit number of Spectators",
    "[TeamHighlight] Highlight",
  ];

  const withMarkedAsUndoneActionsDict = Object.values(actionHistory)
    .sort((v1, v2) => v1.orderRank - v2.orderRank)
    .reduce((collector, current) => {
      const index = current.orderRank;

      // If marked as undone, it has already been handled
      // Also we're looking for an undo action to do something with
      if (
        !collector[index].isUndone &&
        current.type === "[Undoable] Undo last action"
      ) {
        // Start counting how many previous actions are undone
        let undoPreviousCount = current.payload;

        // Start looking ahead to see if any more undoes or redoes follow
        let walkingIndex = index + 1;
        while (undoActionTypes.includes(collector[walkingIndex]?.type)) {
          // We're going to handle this event now, so mark it 'handled'
          collector[walkingIndex].isUndone = true;
          collector[walkingIndex].hideFromHistory = true;
          if (collector[walkingIndex].type === "[Undoable] Undo last action") {
            // Found an undo, so increase the count of undone actions
            undoPreviousCount += (
              collector[
                walkingIndex
              ] as GenericUpdateAction<"[Undoable] Undo last action">
            ).payload;
          }
          if (
            collector[walkingIndex].type === "[Undoable] Redo previous action"
          ) {
            // Found a redo, so decrease the count of undone actions
            undoPreviousCount -= (
              collector[
                walkingIndex
              ] as GenericUpdateAction<"[Undoable] Redo previous action">
            ).payload;
          }
          walkingIndex++;
        }
        // Reuse and set to the previous index to start marking the actions undone
        walkingIndex = index - 1;
        while (undoPreviousCount > 0) {
          try {
            if (
              // Don't undo any [Undoable] actions or actions that are already undone or that can't be undone
              !undoActionTypes.includes(collector[walkingIndex].type) &&
              !collector[walkingIndex].isUndone &&
              !undoableActionTypes.includes(collector[walkingIndex].type)
            ) {
              // Found one that we can undo, set flag and decrease counter
              collector[walkingIndex].isUndone = true;
              collector[walkingIndex].hideFromHistory = true;
              undoPreviousCount--;
            }
            walkingIndex--;
          } catch (e) {
            console.warn(collector);
            console.warn(walkingIndex);
            throw e;
          }
        }

        // Finally, all the work has been done and this action itself can be marked as undone (a.k.a. handled)
        collector[index].isUndone = true;
      }

      // if this is a successful challenge, find the first previous SetScore action and mark it as undone
      if (
        current.type === "[Challenges] Challenge success" &&
        ["POINT_REVERSED", "DOUBLE_FAULT"].includes(current.payload.outcome)
      ) {
        let walkingIndex = index - 1;
        while (walkingIndex > 0) {
          switch (collector[walkingIndex].type) {
            case "[Challenges] Challenge":
              break;
            case "[Set] Score":
              collector[walkingIndex].isUndone = true;
              collector[walkingIndex].hideFromHistory = false;
              walkingIndex = 0;
              break;
            default:
              collector[walkingIndex].isUndone = true;
              collector[walkingIndex].hideFromHistory = false;
              break;
          }
          walkingIndex--;
        }
      }

      return collector;
    }, actionHistory);

  const withMarkedAsUndoneActions = Object.values(
    withMarkedAsUndoneActionsDict,
  );

  const isChecksumValid = !validateActionHistoryChecksum(
    withMarkedAsUndoneActions.slice(0, -1),
    withMarkedAsUndoneActions.slice(-1)[0]?.uuidHistoryChecksum || "",
  );

  const builtState = withMarkedAsUndoneActions.reduce((state, action) => {
    return getUpdatedGameState(action)(state);
  }, defaultGameState());

  const newState = {
    ...useGameState.getInitialState(),
    ...builtState,
    isChecksumValid,
  };
  console.debug("Built game state from history", newState);
  return newState;
};

export const updateGameState = <T extends UpdateActionType>(
  action: GenericUpdateAction<T>,
) =>
  useGameState.setState((currentState) => {
    const isChecksumValid = validateActionHistoryChecksum(
      currentState.actionHistory,
      action.uuidHistoryChecksum || "",
    );
    const updatedGameState = {
      ...getUpdatedGameState(action)(currentState),
      isChecksumValid,
    };
    console.debug("Updated game state", { updatedGameState });
    return updatedGameState;
  });

const getUpdatedGameState =
  <T extends UpdateActionType>(action: GenericUpdateAction<T>) =>
  (state: GameState): GameState => {
    const func: UpdateGameStateFunction<T> = getUpdateFunction(action.type);
    if (func) {
      try {
        return deduplicateAndExecute(func, action)(state);
      } catch (e) {
        console.error("Error updating game state", action);
        throw e;
      }
    } else {
      console.error(`SHOULD NOT HAPPEN!`, action.type);
    }
    return state;
  };

const deduplicateAndExecute =
  <T extends UpdateActionType>(
    fn: UpdateGameStateFunction<T>,
    action: GenericUpdateAction<T>,
  ) =>
  (state: GameState): GameState => {
    if (state.lastAction.orderRank >= action.orderRank) {
      return state;
    }
    const newState = {
      ...state,
      ...fn(action, state),
    } as GameState;

    const lastAction: UpdateActionWithState = {
      ...(action as UpdateAction),
      score: { ...newState.currentScore },
      setNumber: newState.currentSet.setNumber,
      setScore: { ...newState.currentSetScore },
      timeoutsInSet: { ...newState.currentSet.timeoutsUsed },
      nextServe: newState.serving,
    };

    const actionHistory: UpdateActionWithStateDict = {
      ...state.actionHistory,
      [lastAction.orderRank]: lastAction,
    };

    const lastVisibleAction =
      isVisibleActionType(action.type) && !action.isUndone
        ? lastAction
        : state.lastVisibleAction;

    return {
      ...(action.isUndone ? state : newState),
      actionHistory,
      lastAction,
      lastVisibleAction,
    };
  };

const validateActionHistoryChecksum = (
  actionHistory: UpdateActionWithStateDict | UpdateAction[],
  expectedChecksum: string,
) => {
  const uuids = Object.values(actionHistory).map((action) => action.uuid);
  return generateUuidHistoryChecksum(uuids) === expectedChecksum;
};

export const buildGameStateFromHistory = (actionHistory: UpdateAction[]) => {
  return buildGameStateWithActions(
    actionHistory.reduce((collector, current) => {
      collector[current.orderRank] = current;
      return collector;
    }, {} as UpdateActionHistory),
  );
};
